<template>
  <section
    ref="panelRef"
    class="vcp"
    :class="{
      'vcp--expanded': isExpanded,
      'vcp--expandable': body.hasContent
    }"
  >
    <header class="vcp__header">
      <div class="vcp__header-title" @click="toggle">
        <slot name="title" />
      </div>
      <div class="vcp__header-time" @click="toggle">
        <slot name="time" />
      </div>
      <div class="vcp__header-actions">
        <ToggleUp @click="toggle" v-if="isExpanded" />
        <ToggleDown @click="toggle" v-if="!isExpanded" />
        <slot name="actions" />
      </div>
    </header>

    <transition
      :data-key="body.dataKey"
      name="slide"
      @before-enter="collapse"
      @enter="expand"
      @before-leave="expand"
      @leave="collapse"
    >
      <div v-if="isExpanded" ref="bodyRef" class="vcp__body">
        <div ref="bodyContentRef" class="vcp__body-content">
          <slot name="content" />
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import { ToggleUp, ToggleDown } from "@/components/buttons";
import { v4 as uuid } from "uuid";
import {
  computed,
  defineComponent,
  nextTick,
  onUpdated,
  ref,
  onMounted
} from "vue";

import { toggleIcon } from "@/components/collapsible/vue-collapsible-panel.constant";
import { useCollapsiblePanelStore } from "@/components/collapsible/composables/vue-collapsible-panel.store";

export default defineComponent({
  name: "VueCollapsiblePanel",
  props: {
    expanded: Boolean
  },
  components: {
    ToggleUp,
    ToggleDown
  },
  setup(props, context) {
    const idPanel = `panel-${uuid()}`;
    const panelRef = ref();
    const bodyRef = ref();
    const bodyContentRef = ref();
    const {
      panelExpanded,
      togglePanelExpandedStatus,
      setPanelExpandedStatus
    } = useCollapsiblePanelStore();

    const body = computed(() => ({
      hasContent:
        context.slots.content && context.slots.content()[0].children.length > 0,
      dataKey: uuid()
    }));

    const idGroup = computed(() => {
      return panelRef.value?.parentElement?.getAttribute("data-id-group") || "";
    });

    const isExpanded = computed(
      () => panelExpanded(idGroup.value, idPanel).value && body.value.hasContent
    );

    const toggle = () => {
      if (!body.value.hasContent) return;

      togglePanelExpandedStatus(idGroup.value, idPanel);
    };

    const collapse = element => {
      element.style.height = "0";
    };

    const expand = element => {
      element.style.height = `${element.scrollHeight}px`;
    };

    const updateBodyHeight = async () => {
      await nextTick();

      if (!bodyRef.value || !bodyContentRef.value) return;

      bodyRef.value.style.height = `${Math.min(
        bodyContentRef.value.scrollHeight,
        bodyRef.value.scrollHeight
      )}px`;
    };

    onMounted(() => {
      setPanelExpandedStatus(idGroup.value, idPanel, props.expanded);
    });

    onUpdated(() => {
      updateBodyHeight();
    });

    return {
      body,
      panelRef,
      bodyRef,
      bodyContentRef,
      isExpanded,
      collapse,
      expand,
      toggle,
      toggleIcon
    };
  }
});
</script>

<style lang="scss" scoped>
.vcp {
  $root: &;
  $timing-function: cubic-bezier(0.5, 0.25, 0, 1);

  &__header {
    display: flex;
    padding: 12px 0;
    pointer-events: none;
    border-bottom: 2px solid #ffffff;
    height: 75px;
    line-height: 75px;
    justify-content: space-between;
    align-items: center;

    #{$root}--expandable & {
      pointer-events: all;
      cursor: pointer;
    }
  }

  &:last-child {
    .vcp__header {
      border-bottom: 0;
    }
  }

  &__header-time {
    position: relative;
    top: 10px;

    :deep(.time-holder) {
      font-size: 12px;
      line-height: 13px;

      span:first-child {
        width: 32px;
        display: inline-block;
      }

      span:last-child {
        color: $gkt-orange;
        font-weight: 500;
      }
    }
  }

  &__header-title {
    display: block;
    width: calc(100% - 324px);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    position: relative;
  }

  &__header-actions {
    display: flex;
    width: 178px;
  }

  &__body {
    overflow: hidden;
    transition: all 0.3s $timing-function;
  }

  &__body-content {
    padding: 12px 0;
  }
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0.25;
}
</style>
